import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { PerListEntry, } from 'src/app/shared/per-list-entry';
import { KeycloakUser } from 'src/app/shared/keycloak-user';

/** Maps a row from the `SecurityGroups` table — one named access group (ID / name / description / flag). */
export class SecurityGroups {
  SECGROUPID: number;
  SECGROUPNAME: string;
  SECGROUPDESC: string;
  SECGROUPFLAG: number;

  uid?: number;          // Simply for DATAKEY Index in table
  // Editing DATAKEY directly impossible
  // due to Angular checking cycle

  cloneIndex?: number;  // Backlink to copy

}

/** Extends `KeycloakUser` with a `disabled` flag for admin-table display. */
export class KeycloakUsersList extends KeycloakUser {
  disabled?: boolean;
}

/** jsreport user write-model (for create/update calls to the jsreport API); all fields except `username` are optional. */
export interface JsReportUser {
  username: string;
  name?: string;
  password?: string;
  isAdmin?: boolean;
  failedLoginAttemptsCount?: number;
  failedLoginAttemptsStart?: Date;
  editAllPermissions?: boolean;
  readAllPermissions?: boolean;
  folder?: {
    shortid: string;
  };
  tags?: Array<{
    shortid: string;
  }>;
  _id?: string;
  creationDate?: Date;
  modificationDate?: Date;
  shortid?: string;
}

/** jsreport user-group as loaded from the report server during initialisation. */
export interface ReportServerGroups {
  shortid: string;     // Report server group short ID
  name: string;        // Report server group name
  creationDate: string; // Report server group creation date
  modificationDate: string; // Report server group modification date
  _id: string;        // Report server group ID
  users: any[];      // Report server group users
  folder?: { shortid: string; };    // Report server group folders
  isAdmin?: boolean; // Report server group isAdmin flag
};

/** Aggregated representation of one jsreport server instance — URL, users, groups, and position index. */
export interface ReportServer {
  serverName: string;   // Report server name
  serverUrl: string;    // Report server URL
  users: any[];         // Report server users
  groups: ReportServerGroups[];       // Report server groups
  index: number;       // Report server index
};

/** Access record for a single user on a single jsreport server — maps Keycloak login to report-server permissions. */
export interface ReportServerUserAccess {
  serverName: string;   // Report server name
  serverUrl: string;    // Report server URL
  userName: string;     // Report server user username (login)
  password?: string;    // Report server password
  name?: string;        // Report server user name
  kcLogin: string;      // Keycloak login of the user
  readAccess: boolean;  // Maps to Report server user readAllPermissions
  editAccess: boolean;  // Maps to Report server user editPermissions
  isAdmin: boolean;     // Maps to Report server user isAdmin
  userId?: string;      // Report server user ID
  shortId?: string;     // Report server user short ID
  enabled?: boolean;    // Report server user enabled status (by fact there is no such field in Report server API)
  groups?: ReportServerGroups[];
  reportServerInstance?: ReportServer; // Backlink to Report server instance
}


/**
 * Maps a `SecurityGroupMembers` DB row — the full per-user settings record that drives
 * stock, currency, department, and VAT defaults for a given user in the application.
 */
export class SecurityGroupMembers {
  SGMGROUPID: number;
  SGMCLIID: number;
  SGMPERSID: number;
  SGMDESC: string;
  SGMLOGIN: string;
  SGMPASSWD: string;
  mainCurrencyId: number;
  balanceCurrencyId: number;
  viewDCurrencyId: number;
  viewECurrencyId: number;
  mainStockId: number;
  impStockId: number;
  auxStockId: number;
  impCountFlag: boolean;
  auxCountFlag: boolean;
  manDepartmentId: number;
  auxDepartmentId: number;
  paidDepartmentId: number;
  cashDepartmentId: number;
  defaultTradeCompanyId: number;
  vatValue: number;

  uid?: number;
  namestr?: string;
  group?: SecurityGroups;
  groups?: SecurityGroups[];

  personMap?: PerListEntry;  // Mapped Person entry for SGMPERSID
  keycloakGroups?: any[]; // Keycloak groups assigned
  keycloakRoles?: any[];  // Keycloak roles assigned
  keycloakLocked?: boolean; // Keycloak account locked

  // This looks like obsoleted
  reportServerUser?: { [key: string]: any }; // Report server user info

  // New structure for report server access for particular user
  // Contains array of report servers with access info for each
  reportServerAccess?: ReportServerUserAccess[]; // Report server access info

  editing?: boolean;
  cloneIndex?: number;  // Backlink to copy

  /** Constructs a `SecurityGroupMembers` shell populated from an existing `GlobalSettingsData` instance. */
  static mapGlobalSettings(gs: GlobalSettingsData): SecurityGroupMembers {

    return {

      SGMGROUPID: gs.accessGroup,
      SGMCLIID: gs.mainCompanyId,
      SGMPERSID: gs.mainPersonId,
      SGMDESC: null,
      SGMLOGIN: null,
      SGMPASSWD: null,
      mainCurrencyId: gs.mainCurrencyId,
      balanceCurrencyId: gs.balanceCurrencyId,
      viewDCurrencyId: gs.viewDCurrencyId,
      viewECurrencyId: gs.viewECurrencyId,
      mainStockId: gs.mainStockId,
      impStockId: gs.impStockId,
      auxStockId: gs.auxStockId,
      impCountFlag: gs.impCountFlag,
      auxCountFlag: gs.auxCountFlag,
      manDepartmentId: gs.manDepartmentId,
      auxDepartmentId: gs.auxDepartmentId,
      paidDepartmentId: gs.paidDepartmentId,
      cashDepartmentId: gs.cashDepartmentId,
      defaultTradeCompanyId: gs.defaultTradeCompanyId,
      vatValue: gs.vatValue
    };

  }

}

/*

 SELECT TOP (1000) [SECGROUPID]
      ,[SECGROUPNAME]
      ,[SECGROUPDESC]
      ,[SECGROUPFLAG]
  FROM [Document].[dbo].[SecurityGroups]

 SELECT TOP (1000) [SGMGROUPID]
      ,[SGMCLIID]
      ,[SGMPERSID]
      ,[SGMDESC]
      ,[SGMLOGIN]
      ,[SGMPASSWD]
      ,[mainCurrencyId]
      ,[balanceCurrencyId]
      ,[viewDCurrencyId]
      ,[viewECurrencyId]
      ,[mainStockId]
      ,[impStockId]
      ,[auxStockId]
      ,[impCountFlag]
      ,[auxCountFlag]
      ,[manDepartmentId]
      ,[auxDepartmentId]
      ,[paidDepartmentId]
      ,[cashDepartmentId]
      ,[defaultTradeCompanyId]
      ,[vatValue]
  FROM [Document].[dbo].[SecurityGroupMembers]

  */
